import React from "react";
import { Button } from "@/components/ui/button";
import { Share, Mail } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { toast } from "@/components/ui/use-toast";

interface ShareAppointmentProps {
  appointment: any;
}

export const ShareAppointment: React.FC<ShareAppointmentProps> = ({ appointment }) => {
  const formatAppointmentText = (useEmojis: boolean = true) => {
    const appointmentDate = new Date(appointment.date);
    const endDate = appointment.end_date ? new Date(appointment.end_date) : null;
    const hasTime = appointmentDate.getHours() !== 0 || appointmentDate.getMinutes() !== 0;
    
    // Usando emojis mais compatíveis ou texto simples
    const icons = useEmojis ? {
      title: "📅 ",
      type: "📋 Tipo: ",
      date: "📆 Data: ",
      time: "🕐 Horário: ",
      client: "👤 Cliente: ",
      location: "📍 Local: ",
      notes: "📝 Observações: "
    } : {
      title: "*",
      type: "*Tipo:* ",
      date: "*Data:* ",
      time: "*Horário:* ",
      client: "*Cliente:* ",
      location: "*Local:* ",
      notes: "*Observações:* "
    };

    let text = `${icons.title}*${appointment.title}*\n\n`;
    text += `${icons.type}${appointment.type}\n`;
    text += `${icons.date}${format(appointmentDate, "EEEE, d 'de' MMMM 'de' yyyy", { locale: ptBR })}\n`;
    
    if (hasTime) {
      text += `${icons.time}${format(appointmentDate, 'HH:mm')}`;
      if (endDate) {
        text += ` - ${format(endDate, 'HH:mm')}`;
      }
      text += '\n';
    }
    
    if (appointment.clients?.name) {
      text += `${icons.client}${appointment.clients.name}\n`;
    }
    
    if (appointment.location) {
      text += `${icons.location}${appointment.location}\n`;
    }
    
    if (appointment.notes) {
      text += `${icons.notes}${appointment.notes}\n`;
    }
    
    return text.trim();
  };

  const shareViaWhatsApp = () => {
    // Primeira tentativa: com emojis simples
    let text = formatAppointmentText(false); // Sem emojis, apenas formatação
    
    try {
      // Codificar o texto de forma mais segura
      const encodedText = encodeURIComponent(text);
      const whatsappUrl = `https://wa.me/?text=${encodedText}`;
      
      window.open(whatsappUrl, '_blank');
      
      toast({
        title: "Compartilhamento",
        description: "Abrindo WhatsApp para compartilhar o compromisso.",
      });
    } catch (error) {
      console.error('Erro ao compartilhar via WhatsApp:', error);
      
      // Fallback: versão ainda mais simples
      const simpleText = `${appointment.title}\n\nTipo: ${appointment.type}\nData: ${format(new Date(appointment.date), "dd/MM/yyyy", { locale: ptBR })}`;
      const encodedSimpleText = encodeURIComponent(simpleText);
      const fallbackUrl = `https://wa.me/?text=${encodedSimpleText}`;
      
      window.open(fallbackUrl, '_blank');
      
      toast({
        title: "Compartilhamento",
        description: "Abrindo WhatsApp com versão simplificada do compromisso.",
        variant: "default"
      });
    }
  };

  const shareViaEmail = () => {
    const text = formatAppointmentText(false);
    const subject = `Compromisso: ${appointment.title}`;
    
    // Remove formatação markdown para email
    const body = text
      .replace(/\*/g, '')
      .replace(/📅|📋|📆|🕐|👤|📍|📝/g, '');
    
    try {
      const mailtoUrl = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
      window.open(mailtoUrl, '_blank');
      
      toast({
        title: "Compartilhamento",
        description: "Abrindo cliente de email para compartilhar o compromisso.",
      });
    } catch (error) {
      console.error('Erro ao compartilhar via email:', error);
      
      toast({
        title: "Erro",
        description: "Não foi possível abrir o cliente de email.",
        variant: "destructive"
      });
    }
  };

  // Função para copiar texto para área de transferência como fallback
  const copyToClipboard = async () => {
    const text = formatAppointmentText(true);
    
    try {
      await navigator.clipboard.writeText(text);
      toast({
        title: "Copiado!",
        description: "Texto copiado para a área de transferência.",
      });
    } catch (error) {
      console.error('Erro ao copiar:', error);
      toast({
        title: "Erro",
        description: "Não foi possível copiar o texto.",
        variant: "destructive"
      });
    }
  };

  return (
    <div className="flex space-x-2">
      <Button
        variant="outline"
        size="sm"
        onClick={shareViaWhatsApp}
        className="flex items-center space-x-2"
      >
        <Share className="h-4 w-4" />
        <span>WhatsApp</span>
      </Button>
      
      <Button
        variant="outline"
        size="sm"
        onClick={shareViaEmail}
        className="flex items-center space-x-2"
      >
        <Mail className="h-4 w-4" />
        <span>Email</span>
      </Button>
      
      <Button
        variant="outline"
        size="sm"
        onClick={copyToClipboard}
        className="flex items-center space-x-2"
      >
        <Share className="h-4 w-4" />
        <span>Copiar</span>
      </Button>
    </div>
  );
};